/**
 * Zustand store for Financial Diagnostic Wizard state
 */

import { create } from 'zustand';
import { financialDiagnosticApi } from '../api/endpoints/financialDiagnostic';
import logger from '../utils/logger';
import {
  FinancialDiagnosticQuestion,
  FinancialDiagnosticSubmitData,
  FinancialProfile,
  RevenueBreakdownItem,
  DebtBreakdownItem,
  ExpenseBreakdownItem,
  SavingsBreakdownItem,
  SavingsTypesResponse,
} from '../api/types';

interface DiagnosticResult {
  message: string;
  goal_proposal?: {
    title: string;
    target_amount: number;
    deadline: string;
    monthly_effort: number;
    type: string;
    months_needed: number;
  };
  ai_metadata?: {
    provider: string;
    response_time_ms: number;
  };
}

interface FinancialDiagnosticState {
  // Current wizard state
  currentStep: number;
  totalSteps: number;
  currentQuestion: FinancialDiagnosticQuestion | null;

  // Form data
  formData: FinancialDiagnosticSubmitData;

  // Breakdown state (for multi-line input)
  incomeBreakdown: RevenueBreakdownItem[];
  expensesBreakdown: ExpenseBreakdownItem[];
  debtBreakdown: DebtBreakdownItem[];
  savingsBreakdown: SavingsBreakdownItem[];
  showIncomeBreakdown: boolean;
  showExpensesBreakdown: boolean;
  showDebtBreakdown: boolean;
  showSavingsBreakdown: boolean;

  // Savings types (loaded from API based on user's country)
  savingsTypesData: SavingsTypesResponse | null;

  // Diagnostic result
  diagnostic: DiagnosticResult | null;

  // UI state
  isLoading: boolean;
  isStarting: boolean;
  isSubmitting: boolean;
  error: string | null;

  // Completion state
  isComplete: boolean;

  // Actions
  startWizard: () => Promise<void>;
  submitStep: (data: Partial<FinancialDiagnosticSubmitData>) => Promise<void>;
  goToPreviousStep: () => void;
  reset: () => void;
  clearError: () => void;

  // Breakdown actions
  setIncomeBreakdown: (breakdown: RevenueBreakdownItem[]) => void;
  setExpensesBreakdown: (breakdown: ExpenseBreakdownItem[]) => void;
  setDebtBreakdown: (breakdown: DebtBreakdownItem[]) => void;
  setSavingsBreakdown: (breakdown: SavingsBreakdownItem[]) => void;
  toggleIncomeBreakdown: () => void;
  toggleExpensesBreakdown: () => void;
  toggleDebtBreakdown: () => void;
  toggleSavingsBreakdown: () => void;
  loadSavingsTypes: () => Promise<void>;
}

export const useFinancialDiagnosticStore = create<FinancialDiagnosticState>((set, get) => ({
  // Initial state
  currentStep: 0,
  totalSteps: 4, // Backend has 4 steps (monthly_saving_capacity is auto-calculated)
  currentQuestion: null,
  formData: {},
  incomeBreakdown: [{ type: 'salary', amount: 0 }],
  expensesBreakdown: [{ type: 'housing', amount: 0 }],
  debtBreakdown: [{ type: 'mortgage', amount: 0 }],
  savingsBreakdown: [{ type: 'savings_account', amount: 0, availability: 'immediate', category: 'liquid' }],
  // Always show breakdowns by default - user must fill detailed breakdown
  showIncomeBreakdown: true,
  showExpensesBreakdown: true,
  showDebtBreakdown: true,
  showSavingsBreakdown: true,
  savingsTypesData: null,
  diagnostic: null,
  isLoading: false,
  isStarting: false,
  isSubmitting: false,
  error: null,
  isComplete: false,

  // Start the wizard
  startWizard: async () => {
    set({ isStarting: true, error: null });
    try {
      // Load existing profile data, savings types, and start wizard in parallel for better performance
      const [profileResponse, wizardResponse, savingsTypesResponse] = await Promise.all([
        financialDiagnosticApi.getFinancialProfile().catch(() => ({ financial_profile: null })),
        financialDiagnosticApi.startDiagnostic(),
        financialDiagnosticApi.getSavingsTypes().catch(() => null),
      ]);

      const profile = profileResponse.financial_profile;

      // Helper to convert to number (handles strings from backend)
      const toNumber = (val: any) => {
        if (val === null || val === undefined) return undefined;
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? undefined : num;
      };

      const existingData: Partial<FinancialDiagnosticSubmitData> = profile
        ? {
            monthly_income: toNumber(profile.monthly_income),
            monthly_expenses: toNumber(profile.monthly_expenses),
            debt_amount: toNumber(profile.debt_amount),
            current_savings: toNumber(profile.current_savings),
            monthly_saving_capacity: toNumber(profile.monthly_saving_capacity),
          }
        : {};

      // Load existing breakdown data from profile
      const existingIncomeBreakdown = profile?.monthly_income_breakdown?.length
        ? profile.monthly_income_breakdown
        : [{ type: 'salary' as const, amount: 0 }];

      const existingExpensesBreakdown = profile?.monthly_expenses_breakdown?.length
        ? profile.monthly_expenses_breakdown
        : [{ type: 'housing' as const, amount: 0 }];

      const existingDebtBreakdown = profile?.debt_breakdown?.length
        ? profile.debt_breakdown
        : [{ type: 'mortgage' as const, amount: 0 }];

      const existingSavingsBreakdown = profile?.current_savings_breakdown?.length
        ? profile.current_savings_breakdown
        : [{ type: 'savings_account' as const, amount: 0, availability: 'immediate' as const, category: 'liquid' as const }];

      set({
        currentStep: wizardResponse.step,
        totalSteps: wizardResponse.total_steps,
        currentQuestion: wizardResponse.question,
        isStarting: false,
        isComplete: false,
        formData: existingData,
        // Load saved breakdown data
        incomeBreakdown: existingIncomeBreakdown,
        expensesBreakdown: existingExpensesBreakdown,
        debtBreakdown: existingDebtBreakdown,
        savingsBreakdown: existingSavingsBreakdown,
        savingsTypesData: savingsTypesResponse,
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(', ')
          : error.message || 'Impossible de démarrer le diagnostic';
      set({ error: errorMessage, isStarting: false });
    }
  },

  // Submit current step
  submitStep: async (stepData: Partial<FinancialDiagnosticSubmitData>) => {
    // Prevent concurrent submissions
    if (get().isSubmitting) {
      logger.warn('[FinancialDiagnosticStore] Blocked concurrent submission attempt');
      return;
    }

    logger.log('[FinancialDiagnosticStore] Starting submission for step', get().currentStep, 'with data:', stepData);
    set({ isSubmitting: true, error: null });

    const currentFormData = get().formData;
    const updatedFormData = { ...currentFormData, ...stepData };

    // CRITICAL FIX: Only send the CURRENT step's data to avoid premature completion
    // If we send all pre-filled data, the backend will see all 5 fields and mark wizard as complete
    // Remove undefined values before sending to API
    const cleanedStepData = Object.fromEntries(
      Object.entries(stepData).filter(([_, value]) => value !== undefined)
    );

    // Include breakdown data if user expanded multi-line input
    const { showIncomeBreakdown, showExpensesBreakdown, showDebtBreakdown, showSavingsBreakdown, incomeBreakdown, expensesBreakdown, debtBreakdown, savingsBreakdown, currentStep } = get();

    const submitData: FinancialDiagnosticSubmitData & { current_step: number } = {
      ...cleanedStepData, // ← FIXED: Only send current step data, not all formData
      current_step: currentStep,
    };

    // Add income breakdown if on step 1 and breakdown is expanded
    if (currentStep === 1 && showIncomeBreakdown && incomeBreakdown.length > 0) {
      submitData.monthly_income_breakdown = incomeBreakdown;
    }

    // Add expenses breakdown if on step 2 and breakdown is expanded
    if (currentStep === 2 && showExpensesBreakdown && expensesBreakdown.length > 0) {
      submitData.monthly_expenses_breakdown = expensesBreakdown;
    }

    // Add debt breakdown if on step 3 and breakdown is expanded
    if (currentStep === 3 && showDebtBreakdown && debtBreakdown.length > 0) {
      submitData.debt_breakdown = debtBreakdown;
    }

    // Add savings breakdown if on step 4 and breakdown is expanded
    if (currentStep === 4 && showSavingsBreakdown && savingsBreakdown.length > 0) {
      submitData.current_savings_breakdown = savingsBreakdown;
    }

    try {
      const response = await financialDiagnosticApi.submitDiagnostic(submitData);

      // Merge saved profile data from backend with our local formData
      const savedProfile = response.financial_profile;

      // Helper to convert to number (handles strings from backend)
      const toNumber = (val: any) => {
        if (val === null || val === undefined) return undefined;
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? undefined : num;
      };

      const mergedFormData = {
        ...updatedFormData,
        monthly_income: toNumber(savedProfile?.monthly_income) ?? updatedFormData.monthly_income,
        monthly_expenses: toNumber(savedProfile?.monthly_expenses) ?? updatedFormData.monthly_expenses,
        debt_amount: toNumber(savedProfile?.debt_amount) ?? updatedFormData.debt_amount,
        current_savings: toNumber(savedProfile?.current_savings) ?? updatedFormData.current_savings,
        monthly_saving_capacity: toNumber(savedProfile?.monthly_saving_capacity) ?? updatedFormData.monthly_saving_capacity,
      };

      // Batch all state updates into a single set() call for better performance
      if (response.snapshot_complete) {
        // Wizard complete - diagnostic received
        logger.log('[FinancialDiagnosticStore] Wizard complete - setting diagnostic');
        logger.log('[FinancialDiagnosticStore] response.diagnostic:', response.diagnostic);
        logger.log('[FinancialDiagnosticStore] response.snapshot_complete:', response.snapshot_complete);
        const diagnosticData = response.diagnostic
          ? {
              message: response.diagnostic,
              goal_proposal: response.goal_proposal,
              ai_metadata: response.ai_metadata,
            }
          : null;
        logger.log('[FinancialDiagnosticStore] diagnosticData:', diagnosticData);
        set({
          formData: mergedFormData,
          isComplete: true,
          isSubmitting: false,
          diagnostic: diagnosticData,
        });
        logger.log('[FinancialDiagnosticStore] State updated - isComplete: true');
      } else {
        // Move to next step
        const nextStep = response.current_step || get().currentStep + 1;

        // Safety check: don't go beyond step 4 (backend only has 4 steps)
        if (nextStep > 4) {
          logger.error('[FinancialDiagnosticStore] Error: nextStep > 4');
          set({
            formData: mergedFormData,
            error: 'Erreur: tous les champs doivent être remplis pour continuer.',
            isSubmitting: false,
            currentStep: 4,
          });
          return;
        }

        logger.log('[FinancialDiagnosticStore] Moving to step', nextStep);
        // CRITICAL: Batch ALL state updates in a single set() call to prevent double re-renders
        set({
          formData: mergedFormData,
          currentStep: nextStep,
          currentQuestion: response.question || getQuestionForStep(nextStep),
          isSubmitting: false,
        });
      }
    } catch (error: any) {
      logger.error('[FinancialDiagnosticStore] Submission error:', error);
      const errorMessage =
        error?.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(', ')
          : error.message || 'Impossible de soumettre les données';
      set({ error: errorMessage, isSubmitting: false });
    }
  },

  // Go to previous step
  goToPreviousStep: () => {
    const currentStep = get().currentStep;

    if (currentStep <= 1) {
      return; // Can't go back from step 1
    }

    const previousStep = currentStep - 1;
    const previousQuestion = getQuestionForStep(previousStep);

    set({
      currentStep: previousStep,
      currentQuestion: previousQuestion,
      error: null,
    });
  },

  // Reset wizard
  reset: () => {
    set({
      currentStep: 0,
      totalSteps: 4, // Backend has 4 steps (monthly_saving_capacity is auto-calculated)
      currentQuestion: null,
      formData: {},
      incomeBreakdown: [{ type: 'salary', amount: 0 }],
      expensesBreakdown: [{ type: 'housing', amount: 0 }],
      debtBreakdown: [{ type: 'mortgage', amount: 0 }],
      savingsBreakdown: [{ type: 'savings_account', amount: 0, availability: 'immediate', category: 'liquid' }],
      // Always show breakdowns by default - user must fill detailed breakdown
      showIncomeBreakdown: true,
      showExpensesBreakdown: true,
      showDebtBreakdown: true,
      showSavingsBreakdown: true,
      savingsTypesData: null,
      diagnostic: null,
      isLoading: false,
      isStarting: false,
      isSubmitting: false,
      error: null,
      isComplete: false,
    });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Breakdown actions
  setIncomeBreakdown: (breakdown: RevenueBreakdownItem[]) => {
    set({ incomeBreakdown: breakdown });
  },

  setExpensesBreakdown: (breakdown: ExpenseBreakdownItem[]) => {
    set({ expensesBreakdown: breakdown });
  },

  setDebtBreakdown: (breakdown: DebtBreakdownItem[]) => {
    set({ debtBreakdown: breakdown });
  },

  setSavingsBreakdown: (breakdown: SavingsBreakdownItem[]) => {
    set({ savingsBreakdown: breakdown });
  },

  toggleIncomeBreakdown: () => {
    set((state) => ({ showIncomeBreakdown: !state.showIncomeBreakdown }));
  },

  toggleExpensesBreakdown: () => {
    set((state) => ({ showExpensesBreakdown: !state.showExpensesBreakdown }));
  },

  toggleDebtBreakdown: () => {
    set((state) => ({ showDebtBreakdown: !state.showDebtBreakdown }));
  },

  toggleSavingsBreakdown: () => {
    set((state) => ({ showSavingsBreakdown: !state.showSavingsBreakdown }));
  },

  loadSavingsTypes: async () => {
    try {
      const response = await financialDiagnosticApi.getSavingsTypes();
      set({ savingsTypesData: response });
    } catch (error) {
      logger.error('Failed to load savings types:', error);
    }
  },
}));

// Helper function to get question for a specific step
function getQuestionForStep(step: number): FinancialDiagnosticQuestion {
  const questions: Record<number, FinancialDiagnosticQuestion> = {
    1: {
      field: 'monthly_income',
      label: 'Quel est le montant total de tes revenus mensuels ?',
      placeholder: 'ex: 2500',
      input_type: 'currency',
      unit: '€',
    },
    2: {
      field: 'monthly_expenses',
      label: 'Quel est le montant total de tes dépenses mensuelles ?',
      placeholder: 'ex: 1800',
      input_type: 'currency',
      unit: '€',
    },
    3: {
      field: 'debt_amount',
      label: 'Quel est le montant total de tes dettes ?',
      placeholder: 'ex: 5000 (ou 0 si aucune)',
      input_type: 'currency',
      unit: '€',
    },
    4: {
      field: 'current_savings',
      label: 'Quel est le montant total de ton épargne actuelle ?',
      placeholder: 'ex: 3000',
      input_type: 'currency',
      unit: '€',
    },
    // Step 5 is deprecated - monthly_saving_capacity is now auto-calculated by backend
    // Kept for backwards compatibility fallback only
    5: {
      field: 'monthly_saving_capacity',
      label: 'Quelle est ta capacité d\'épargne mensuelle ?',
      placeholder: 'ex: 200',
      input_type: 'currency',
      unit: '€',
      help_text: 'Combien peux-tu mettre de côté chaque mois en moyenne ?',
    },
  };

  return questions[step] || questions[1];
}

// Legacy alias for backward compatibility
/** @deprecated Use useFinancialDiagnosticStore instead */
export const useSnapshotStore = useFinancialDiagnosticStore;
